import { PERMISSION_ABILITIES } from '@/config/research';

const initialLists = {
  repository: [],
  project: [],
  site: [],
};

const mockedDependencies = {
  repository: {
    Role: {
      view: 'Repository',
    },
    Tool: {
      view: 'Repository',
    },
    Form: {
      view: 'Repository',
    },
    Event: {
      edit: 'Pipeline',
    },
    Subject: {
      view: 'Event',
    },
  },
  project: {
    Subject: {
      view: 'Project',
    },
    Result: {
      view: 'Event',
    },
    Site: {
      view: 'Member',
    },
    Event: {
      edit: 'Site',
    },
  },
};

export const RepositoryRolePermissions = {
  namespaced: true,

  state: {
    abilityDependencies: { ...initialLists },
    abilitiesByScope: { ...initialLists },
    loadedAbilitiesByScope: false,
    permissions: {},
    loading: false,
    saving: false,
    scopes: [],
  },

  getters: {
    arePermissionsLoaded: (state) => (uuid, role) =>
      Object.keys(state.permissions).includes(uuid) && Object.keys(state.permissions[uuid]).includes(role),

    getPermissions: (state) => (uuid, role) => state.permissions[uuid]?.[role] ?? [],

    getAbilityDependencies: (state) => (scope) => state.abilityDependencies[scope],

    getScopeAbilities: (state) => (scope) => state.abilitiesByScope[scope],

    loadedAbilitiesByScope: (state) => state.loadedAbilitiesByScope,

    arePermissionsLoading: (state) => state.loading,

    getAvailableScopes: ({ scopes }) => scopes,

    allUnselectedPermissionsPayload: (_, { getAvailableScopes: scope }) => {
      return Object.values(PERMISSION_ABILITIES).map((permission) => ({
        allowed: false,
        permission,
        scope,
      }));
    },

    allSelectedPermissionsPayload: (_, { getAvailableScopes: scope }) => {
      return Object.values(PERMISSION_ABILITIES).map((permission) => ({
        allowed: true,
        permission,
        scope,
      }));
    },
  },

  mutations: {
    updatePermissions(state, { uuid, role, permissions }) {
      const currentPermissions = { ...state.permissions[uuid] };

      const newPermissions = { ...currentPermissions, [role]: [...permissions] };

      state.permissions = { ...state.permissions, [uuid]: { ...newPermissions } };
    },

    setAbilityDependencies(state, abilityDependencies) {
      Vue.set(state, 'abilityDependencies', abilityDependencies);
    },

    setAbilitiesByScope(state, abilitiesByScope) {
      Vue.set(state, 'abilitiesByScope', abilitiesByScope);
    },

    setLoadedAbilitiesByScope(state, bool) {
      Vue.set(state, 'loadedAbilitiesByScope', bool);
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setScopes(state, scopes) {
      state.scopes = [...scopes];
    },

    setSaving(state, saving) {
      state.saving = saving;
    },
  },

  actions: {
    async loadPermissions({ commit }, { repository, role }) {
      let permissions = {};
      let scopes = [];

      commit('setLoading', true);
      try {
        const { data } = await this._vm.$api('repositories.roles.permissions.index', {
          version: '1.0',
          repository,
          role,
        });

        permissions = data.data;
        scopes = data.scopes;
      } catch (err) {
        console.error(err);
      }

      commit('updatePermissions', { uuid: repository, role, permissions });
      commit('setScopes', scopes);
      commit('setLoading', false);
    },

    async savePermissions({ commit }, { repository, role, permissions, comment }) {
      commit('setSaving', true);

      try {
        const { data } = await this._vm.$api(
          'repositories.roles.permissions.store',
          {
            version: '1.0',
            repository,
            role,
          },
          { permissions, comment }
        );

        commit('updatePermissions', { uuid: repository, role, permissions: data.data });
      } catch (err) {
        console.error(err);
      }

      commit('setSaving', false);
    },

    async loadPermissionsByScope({ commit }, { repository }) {
      commit('setLoadedAbilitiesByScope', false);
      let abilityDependencies = { ...initialLists };
      let abilitiesByScope = { ...initialLists };

      try {
        const {
          data: { data: abilities, dependencies },
        } = await this._vm.$api('permissions.scopes', {
          repository,
          version: '1.0',
        });
        abilityDependencies = dependencies || mockedDependencies;
        abilitiesByScope = abilities;
      } catch (err) {
        console.error(err);
      }
      commit('setAbilityDependencies', abilityDependencies);
      commit('setAbilitiesByScope', abilitiesByScope);
      commit('setLoadedAbilitiesByScope', true);
    },
  },
};
